// PrivacySettings Component - Manage user privacy preferences + GoodWork Integration

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Shield,
  Eye,
  EyeOff,
  Search,
  Star,
  Heart,
  MapPin,
  Save,
  User,
  Download,
  Sparkles,
  Check,
  AlertCircle,
  X,
  Brain,
  Target,
  Compass,
  ThumbsUp,
  ThumbsDown,
  CheckCircle,
  XCircle,
  Activity,
  Zap,
  MessageCircle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  PublicUserProfile,
  PrivacySettings as PrivacySettingsType,
  DEFAULT_PRIVACY_SETTINGS,
  MessagePrivacySetting,
} from '../../types';
import {
  getOrCreateUserProfile,
  updateUserProfile,
  updatePrivacySettings,
  ensureSearchableField,
} from '../../services/userSearchService';
import { CraftingTeaser } from '../integration/CraftingTeaser';
import {
  hasCraftingProfile,
  getImportableData,
  toCraftingData,
  generateInterests,
  getDataSummary,
  type ImportableData,
} from '../../services/craftingIntegrationService';

interface PrivacySettingsProps {
  onBack: () => void;
}

export function PrivacySettings({ onBack }: PrivacySettingsProps) {
  const { user, updateDisplayName } = useAuth();
  const [profile, setProfile] = useState<PublicUserProfile | null>(null);
  const [privacy, setPrivacy] = useState<PrivacySettingsType>(DEFAULT_PRIVACY_SETTINGS);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [interests, setInterests] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // GoodWork Crafting integration
  const [hasCrafting, setHasCrafting] = useState(false);
  const [craftingData, setCraftingData] = useState<ImportableData | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [craftingImported, setCraftingImported] = useState(false);

  // Import selections
  const [importSelections, setImportSelections] = useState({
    name: true,
    personality: true,
    motivation: true,
    riasec: true,
    futureSkills: true,
    valou: true,
  });

  useEffect(() => {
    if (!user) return;

    const loadProfile = async () => {
      try {
        const userProfile = await getOrCreateUserProfile(
          user.uid,
          user.displayName || 'Anonymer Nutzer',
          user.photoURL || undefined
        );
        setProfile(userProfile);
        setPrivacy(userProfile.privacy || DEFAULT_PRIVACY_SETTINGS);
        setDisplayName(userProfile.displayName || user.displayName || '');
        setBio(userProfile.bio || '');
        setInterests(userProfile.interests?.join(', ') || '');
        setCraftingImported(userProfile.craftingLinked || false);

        // Check for GoodWork Crafting profile
        try {
          const hasCraftingData = await hasCraftingProfile(user.uid);
          setHasCrafting(hasCraftingData);

          if (hasCraftingData) {
            const importable = await getImportableData(user.uid);
            setCraftingData(importable);
          }
        } catch {
          setHasCrafting(false);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleToggle = (key: keyof PrivacySettingsType) => {
    setPrivacy((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setSaved(false);
  };

  const handleOpenImportModal = () => {
    setShowImportModal(true);
  };

  const handleImport = async () => {
    if (!user || !craftingData) return;

    setImportLoading(true);
    try {
      // Import name if selected and available
      if (importSelections.name && craftingData.name) {
        setDisplayName(craftingData.name);
      }

      // Generate interests from selected categories
      const newInterests = generateInterests(craftingData, {
        skills: importSelections.futureSkills,
        values: importSelections.valou,
        strengths: false,
      });

      if (newInterests.length > 0) {
        const currentInterests = interests
          .split(',')
          .map((i) => i.trim())
          .filter((i) => i.length > 0);
        const mergedInterests = [...new Set([...currentInterests, ...newInterests])];
        setInterests(mergedInterests.join(', '));
      }

      // Store crafting data for personalized recommendations
      const storedCraftingData = toCraftingData(craftingData, {
        personality: importSelections.personality,
        motivation: importSelections.motivation,
        riasec: importSelections.riasec,
        valou: importSelections.valou,
      });

      await updateUserProfile(user.uid, {
        craftingLinked: true,
        craftingData: storedCraftingData,
      });

      setCraftingImported(true);
      setShowImportModal(false);
      setSaved(false);
    } catch (error) {
      console.error('Error importing crafting data:', error);
    } finally {
      setImportLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user || !profile) return;

    setSaving(true);
    try {
      const interestList = interests
        .split(',')
        .map((i) => i.trim())
        .filter((i) => i.length > 0);

      const finalDisplayName = displayName.trim() || 'Anonymer Nutzer';

      // Update Firebase Auth display name if changed
      if (finalDisplayName !== user.displayName) {
        await updateDisplayName(finalDisplayName);
      }

      // Update profile in Firestore
      await updateUserProfile(user.uid, {
        bio: bio.slice(0, 200),
        interests: interestList,
        displayName: finalDisplayName,
        displayNameLower: finalDisplayName.toLowerCase(),
      });

      // Update privacy settings
      await updatePrivacySettings(user.uid, privacy);

      // Ensure search field exists if searchable
      if (privacy.searchable) {
        await ensureSearchableField(user.uid, finalDisplayName);
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Bitte melde dich an.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const dataSummary = craftingData ? getDataSummary(craftingData) : null;

  const toggleItems = [
    {
      key: 'searchable' as keyof PrivacySettingsType,
      icon: Search,
      title: 'In Suche sichtbar',
      description: 'Andere Nutzer können dich finden',
    },
    {
      key: 'showReviews' as keyof PrivacySettingsType,
      icon: Star,
      title: 'Bewertungen zeigen',
      description: 'Deine Bewertungen sind öffentlich sichtbar',
    },
    {
      key: 'showFavorites' as keyof PrivacySettingsType,
      icon: Heart,
      title: 'Favoriten zeigen',
      description: 'Deine Lieblingsorte sind öffentlich sichtbar',
    },
    {
      key: 'showCheckins' as keyof PrivacySettingsType,
      icon: MapPin,
      title: 'Check-ins zeigen',
      description: 'Deine Check-ins sind öffentlich sichtbar',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Privatsphäre</h1>
              <p className="text-sm text-gray-500">Verwalte dein öffentliches Profil</p>
            </div>
            <Shield className="w-6 h-6 text-indigo-600" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* GoodWork Crafting Integration */}
        {hasCrafting && craftingData && (
          <div className={`rounded-xl border p-4 ${craftingImported ? 'bg-green-50 border-green-200' : 'bg-indigo-50 border-indigo-200'}`}>
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full ${craftingImported ? 'bg-green-100' : 'bg-indigo-100'}`}>
                {craftingImported ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                )}
              </div>
              <div className="flex-1">
                <h3 className={`font-semibold ${craftingImported ? 'text-green-800' : 'text-indigo-800'}`}>
                  {craftingImported ? 'GoodWork Crafting verbunden' : 'GoodWork Crafting Profil gefunden'}
                </h3>
                <p className={`text-sm mt-1 ${craftingImported ? 'text-green-600' : 'text-indigo-600'}`}>
                  {craftingImported
                    ? 'Deine Crafting-Daten werden für personalisierte Empfehlungen genutzt.'
                    : `Hallo ${craftingData.name || 'User'}! Importiere deine Daten aus GoodWork Crafting.`}
                </p>

                {/* Data Preview */}
                {dataSummary && !craftingImported && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {dataSummary.hasName && (
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">Name</span>
                    )}
                    {dataSummary.hasPersonality && (
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">Big Five</span>
                    )}
                    {dataSummary.hasMotivation && (
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">Motivation</span>
                    )}
                    {dataSummary.hasRiasec && (
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">RIASEC</span>
                    )}
                    {dataSummary.hasValou && (
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">Valou-Styling</span>
                    )}
                    {dataSummary.hasValouMoods && (
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">Valou Moods</span>
                    )}
                    {dataSummary.hasMentalLoad && (
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">Mental Load</span>
                    )}
                  </div>
                )}

                <button
                  onClick={handleOpenImportModal}
                  className={`mt-3 flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                    craftingImported
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-indigo-500 text-white hover:bg-indigo-600'
                  }`}
                >
                  <Download className="w-4 h-4" />
                  {craftingImported ? 'Erneut importieren' : 'Daten importieren'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Show teaser if no Crafting profile */}
        {!hasCrafting && (
          <CraftingTeaser variant="card" showDismiss={false} />
        )}

        {/* Profile Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Profil-Informationen
          </h2>

          {/* Display Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setSaved(false);
              }}
              placeholder="Dein Name"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            {!displayName && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Ein Name wird für Check-ins und Bewertungen benötigt
              </p>
            )}
          </div>

          {/* Bio */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Über mich (max. 200 Zeichen)
            </label>
            <textarea
              value={bio}
              onChange={(e) => {
                setBio(e.target.value.slice(0, 200));
                setSaved(false);
              }}
              placeholder="Erzähl etwas über dich..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              rows={3}
            />
            <p className="text-xs text-gray-400 mt-1">{bio.length}/200</p>
          </div>

          {/* Interests */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Interessen (kommagetrennt)
            </label>
            <input
              type="text"
              value={interests}
              onChange={(e) => {
                setInterests(e.target.value);
                setSaved(false);
              }}
              placeholder="z.B. Kaffee, Bücher, Musik, Coworking"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Privacy Toggles */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Sichtbarkeit
          </h2>

          <div className="space-y-4">
            {toggleItems.map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
              >
                <div className="flex items-start gap-3">
                  <item.icon className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">{item.title}</p>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggle(item.key)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    privacy && privacy[item.key] ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      privacy && privacy[item.key] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>

          {privacy && !privacy.searchable && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-start gap-2">
              <EyeOff className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600">
                Dein Profil ist derzeit nicht öffentlich sichtbar. Aktiviere "In Suche
                sichtbar", damit andere dich finden können.
              </p>
            </div>
          )}
        </div>

        {/* Messaging Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Nachrichten
          </h2>

          <div className="space-y-3">
            <p className="text-sm text-gray-600 mb-4">
              Wer darf dir Direktnachrichten senden?
            </p>

            {[
              { value: 'everyone' as MessagePrivacySetting, label: 'Alle', description: 'Jeder kann dir schreiben' },
              { value: 'mutual' as MessagePrivacySetting, label: 'Gegenseitig', description: 'Nur Personen, mit denen du auch schreibst' },
              { value: 'none' as MessagePrivacySetting, label: 'Niemand', description: 'Keine Nachrichten empfangen' },
            ].map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  privacy?.allowMessages === option.value
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="allowMessages"
                  value={option.value}
                  checked={privacy?.allowMessages === option.value}
                  onChange={() => {
                    setPrivacy((prev) => ({ ...prev, allowMessages: option.value }));
                    setSaved(false);
                  }}
                  className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <p className="font-medium text-gray-900">{option.label}</p>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${
            saved
              ? 'bg-green-500 text-white'
              : 'bg-indigo-500 text-white hover:bg-indigo-600'
          }`}
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : saved ? (
            <>
              <Save className="w-5 h-5" />
              Gespeichert!
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Einstellungen speichern
            </>
          )}
        </button>
      </main>

      {/* Import Modal */}
      {showImportModal && craftingData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowImportModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Daten importieren</h2>
                <p className="text-sm text-gray-500">Wähle, welche Daten du übernehmen möchtest</p>
              </div>
              <button
                onClick={() => setShowImportModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4">
              {/* Name */}
              {craftingData.name && (
                <ImportOption
                  icon={User}
                  title="Name"
                  description={craftingData.name}
                  checked={importSelections.name}
                  onChange={(checked) => setImportSelections(prev => ({ ...prev, name: checked }))}
                />
              )}

              {/* Personality (Big Five) */}
              {craftingData.personality?.bigFiveScores && (
                <ImportOption
                  icon={Brain}
                  title="Persönlichkeit (Big Five)"
                  description={craftingData.personality.bigFiveScores.map(b => b.label).slice(0, 3).join(', ')}
                  checked={importSelections.personality}
                  onChange={(checked) => setImportSelections(prev => ({ ...prev, personality: checked }))}
                />
              )}

              {/* Motivation */}
              {craftingData.motivation && craftingData.motivation.topMotives.length > 0 && (
                <ImportOption
                  icon={Target}
                  title="Motivation"
                  description={craftingData.motivation.topMotives.join(', ')}
                  checked={importSelections.motivation}
                  onChange={(checked) => setImportSelections(prev => ({ ...prev, motivation: checked }))}
                />
              )}

              {/* RIASEC */}
              {craftingData.riasec?.hollandType && (
                <ImportOption
                  icon={Compass}
                  title="RIASEC-Typ"
                  description={`${craftingData.riasec.hollandType} (${craftingData.riasec.hollandCode || ''})`}
                  checked={importSelections.riasec}
                  onChange={(checked) => setImportSelections(prev => ({ ...prev, riasec: checked }))}
                />
              )}

              {/* Future Skills */}
              {craftingData.futureSkills && craftingData.futureSkills.topSkills.length > 0 && (
                <ImportOption
                  icon={Zap}
                  title={`Zukunfts-Skills (${craftingData.futureSkills.topSkills.length})`}
                  description={craftingData.futureSkills.topSkills.slice(0, 5).join(', ')}
                  checked={importSelections.futureSkills}
                  onChange={(checked) => setImportSelections(prev => ({ ...prev, futureSkills: checked }))}
                  note="Werden als Interessen hinzugefügt"
                />
              )}

              {/* Valou Preferences */}
              {craftingData.valouPreferences && (
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={importSelections.valou}
                      onChange={(e) => setImportSelections(prev => ({ ...prev, valou: e.target.checked }))}
                      className="mt-1 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-indigo-600" />
                        <span className="font-medium text-gray-900">Valou-Styling Präferenzen</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">Für personalisierte Ort-Empfehlungen</p>

                      <div className="mt-3 grid grid-cols-2 gap-2">
                        {craftingData.valouPreferences.vorlieben.length > 0 && (
                          <div className="flex items-start gap-1.5">
                            <ThumbsUp className="w-3.5 h-3.5 text-green-500 mt-0.5" />
                            <div>
                              <span className="text-xs font-medium text-green-700">Vorlieben</span>
                              <p className="text-xs text-gray-500">{craftingData.valouPreferences.vorlieben.length} Einträge</p>
                            </div>
                          </div>
                        )}
                        {craftingData.valouPreferences.abneigungen.length > 0 && (
                          <div className="flex items-start gap-1.5">
                            <ThumbsDown className="w-3.5 h-3.5 text-red-500 mt-0.5" />
                            <div>
                              <span className="text-xs font-medium text-red-700">Abneigungen</span>
                              <p className="text-xs text-gray-500">{craftingData.valouPreferences.abneigungen.length} Einträge</p>
                            </div>
                          </div>
                        )}
                        {craftingData.valouPreferences.mustHaves.length > 0 && (
                          <div className="flex items-start gap-1.5">
                            <CheckCircle className="w-3.5 h-3.5 text-indigo-500 mt-0.5" />
                            <div>
                              <span className="text-xs font-medium text-indigo-700">Must-Haves</span>
                              <p className="text-xs text-gray-500">{craftingData.valouPreferences.mustHaves.length} Einträge</p>
                            </div>
                          </div>
                        )}
                        {craftingData.valouPreferences.noGos.length > 0 && (
                          <div className="flex items-start gap-1.5">
                            <XCircle className="w-3.5 h-3.5 text-orange-500 mt-0.5" />
                            <div>
                              <span className="text-xs font-medium text-orange-700">No-Gos</span>
                              <p className="text-xs text-gray-500">{craftingData.valouPreferences.noGos.length} Einträge</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Valou Moods & Mental Load Info */}
              {(craftingData.valouMoods || craftingData.averageMentalLoad !== null) && (
                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-4 h-4 text-indigo-600" />
                    <span className="font-medium text-gray-900">Aktuelle Werte (automatisch)</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Diese Daten werden automatisch für personalisierte Empfehlungen verwendet:
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {craftingData.valouMoods && (
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                        Valou Moods
                      </span>
                    )}
                    {craftingData.averageMentalLoad !== null && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                        Mental Load: {craftingData.averageMentalLoad}%
                      </span>
                    )}
                    {craftingData.currentWellbeing !== null && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        Wohlbefinden: {craftingData.currentWellbeing}/10
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
              <button
                onClick={() => setShowImportModal(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleImport}
                disabled={importLoading}
                className="flex-1 py-2.5 bg-indigo-500 text-white rounded-lg font-medium hover:bg-indigo-600 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {importLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Importieren
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Import Option Component
function ImportOption({
  icon: Icon,
  title,
  description,
  checked,
  onChange,
  note,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  note?: string;
}) {
  return (
    <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
      />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-indigo-600" />
          <span className="font-medium text-gray-900">{title}</span>
        </div>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
        {note && <p className="text-xs text-indigo-600 mt-1">{note}</p>}
      </div>
    </label>
  );
}

export default PrivacySettings;
